import { useState, useEffect } from 'react';

// components
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import Sms from './Sms';
import Link from 'next/link';
import { Icon } from '@/components/ui/icon';

// utilities
import { getImagePath } from '@/utils/images';
import { formatCurrency } from '@/utils/formatting';

const SmsCaseStudyCarousel = ({ caseStudies }) => {
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    // Initialize state from external carousel API
    // setTimeout defers state updates to avoid cascading renders
    setTimeout(() => {
      setCurrent(api.selectedScrollSnap() + 1);
    }, 0);

    // Subscribe to carousel events (external system updates)
    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', handleSelect);

    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  const onSelectItem = (e) => {
    const index = Number(e.currentTarget.dataset.id) || 0;

    if (api) {
      api.scrollTo(index);
    }
  };

  return (
    <VisibleLoading delay={300}>
      {({ ref, className }) => (
        <div ref={ref} className={`my-16 w-full xl:my-32 ${className || ''}`.trim()}>
          <Carousel
            setApi={setApi}
            opts={{
              align: 'center',
              containScroll: false
            }}
          >
            <CarouselContent data-variant="light" className="-ml-4 xl:-ml-10">
              {(caseStudies || []).map((caseStudy) => (
                <CarouselItem
                  key={caseStudy.id}
                  className="max-w-[90vw] pl-4 xl:max-w-[calc(var(--container-width)_+_40px)] xl:pl-10"
                >
                  <CaseStudy caseStudy={caseStudy} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="mt-5 flex justify-center gap-5 lg:mt-12 lg:gap-10">
            {(caseStudies || []).map((_, index) => (
              <div
                key={index}
                data-id={index}
                className="group flex cursor-pointer items-center justify-center p-2"
                onClick={onSelectItem}
              >
                <div
                  className={`h-[8px] w-[8px] cursor-pointer rounded-full lg:h-[16px] lg:w-[16px] ${current === index + 1 ? 'bg-brand-500' : 'bg-grey-500'} `}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </VisibleLoading>
  );
};
export default SmsCaseStudyCarousel;

const CaseStudy = ({ caseStudy }) => {
  return (
    <div
      className={`flex h-full w-full min-w-full flex-col-reverse gap-6 rounded-4xl bg-white p-5 text-black lg:flex-row lg:p-16`}
    >
      <div className="flex-1">
        <div className="txt-md font-semibold tracking-widest uppercase">{caseStudy.subtitle}</div>

        <h2 className="mt-2 text-left text-xl font-light tracking-tight lg:text-3xl">{caseStudy.title}</h2>

        <p className="txt-xl mt-8 hidden lg:block">{caseStudy.text}</p>

        <div className="my-10 flex max-w-68 flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex items-center rounded-lg border border-gray-200 px-4 py-2">
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21.41 11.9124L12.58 3.08244C12.21 2.71244 11.7 2.50244 11.17 2.50244H4C2.9 2.50244 2 3.40244 2 4.50244V11.6724C2 12.2024 2.21 12.7124 2.59 13.0824L11.42 21.9124C12.2 22.6924 13.47 22.6924 14.25 21.9124L21.42 14.7424C22.2 13.9624 22.2 12.7024 21.41 11.9124ZM6.5 8.50244C5.67 8.50244 5 7.83244 5 7.00244C5 6.17244 5.67 5.50244 6.5 5.50244C7.33 5.50244 8 6.17244 8 7.00244C8 7.83244 7.33 8.50244 6.5 8.50244Z"
                  fill="#893BE2"
                />
              </svg>
            </div>

            <div className="flex flex-1 flex-col rounded-lg border border-gray-200 px-4 py-2">
              <div className="text-2xl font-bold">
                {formatCurrency(caseStudy.priceNumber, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs font-semibold tracking-widest text-gray-500 text-gray-950 uppercase">
                {caseStudy.priceText}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex flex-1 flex-col rounded-lg border border-gray-200 px-4 py-2">
              <div className="text-2xl font-bold">
                {formatCurrency(caseStudy.revenueNumber, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
              <div className="text-xs font-semibold tracking-widest text-gray-500 text-gray-950 uppercase">
                {caseStudy.revenueText}
              </div>
            </div>

            <div className="flex items-center rounded-lg border border-gray-200 px-4 py-2">
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.89 11.6C11.11 11.01 10.25 10.64 10.25 9.7C10.25 8.68 11.36 8.31 12.06 8.31C13.37 8.31 13.85 9.3 13.96 9.65L15.54 8.98C15.39 8.53 14.72 7.06 13 6.74V5.5H11V6.76C8.52 7.32 8.51 9.62 8.51 9.72C8.51 11.99 10.76 12.63 11.86 13.03C13.44 13.59 14.14 14.1 14.14 15.06C14.14 16.19 13.09 16.67 12.16 16.67C10.34 16.67 9.82 14.8 9.76 14.58L8.1 15.25C8.73 17.44 10.38 18.03 11 18.21V19.5H13V18.26C13.4 18.17 15.9 17.67 15.9 15.04C15.9 13.65 15.29 12.43 12.89 11.6ZM3 21.5H1V15.5H7V17.5H4.52C6.13 19.91 8.88 21.5 12 21.5C16.97 21.5 21 17.47 21 12.5H23C23 18.58 18.08 23.5 12 23.5C8.28 23.5 4.99 21.65 3 18.83V21.5ZM1 12.5C1 6.42 5.92 1.5 12 1.5C15.72 1.5 19.01 3.35 21 6.17V3.5H23V9.5H17V7.5H19.48C17.87 5.09 15.12 3.5 12 3.5C7.03 3.5 3 7.53 3 12.5H1Z"
                  fill="#07C276"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="mt-10 flex justify-center lg:justify-start">
          {caseStudy.button && (
            <Button
              key={caseStudy.button.id}
              variant={caseStudy.button.variant}
              size={caseStudy.button.size}
              asChild={true}
              className="basis-full xl:basis-auto"
            >
              <Link href={caseStudy.button.url || ''}>
                <Icon type={caseStudy.button.icon} /> {caseStudy.button.label}
              </Link>
            </Button>
          )}
        </div>
      </div>
      <div className="align-center flex flex-1 flex-col justify-center lg:pl-32">
        <div className="relative mx-auto">
          {caseStudy.image && (
            <Image
              src={getImagePath(caseStudy.image)}
              width={caseStudy.image.width}
              height={caseStudy.image.height}
              alt={caseStudy.eshopName}
              className="h-[300px] w-[300px] rounded-4xl object-cover lg:h-[410px] lg:w-[410px]"
            />
          )}

          {caseStudy.sms && (
            <VisibleLoading delay={200} direction="right">
              {({ ref, className }) => (
                <Sms
                  innerRef={ref}
                  {...caseStudy.sms}
                  classNameContainer={`${className} top-[185px] left-[-15px] xl:left-[-15px]`}
                />
              )}
            </VisibleLoading>
          )}
        </div>

        {caseStudy.eshopName && (
          <div className="mt-6 hidden lg:block">
            <p className="txt-md mx-auto max-w-[270px]">
              <strong>
                {caseStudy.eshopName}
                {caseStudy.eshopDescription ? ' - ' : ''}
              </strong>
              {caseStudy.eshopDescription}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
